import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { TeamGrid } from "@/components/TeamGrid";
import { team } from "@/data/team";

// Team data is imported from src/data/team

export default function About() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Align with RTL like Index page does
  useEffect(() => {
    document.documentElement.dir = i18n.language === "he" ? "rtl" : "ltr";
    document.body.classList.toggle("rtl", i18n.language === "he");
    return () => {
      document.documentElement.dir = "ltr";
      document.body.classList.remove("rtl");
    };
  }, [i18n.language]);

  // Handle scroll to anchor when navigating from team cards
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50 h-16 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <img src="/mishu_svg.svg" alt="mishu" className="h-8 w-auto" />
        </div>
        <div className="flex items-center space-x-4">
          <Button asChild variant="ghost">
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("about.backToHome")}
            </Link>
          </Button>
          <LanguageSwitcher />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 pt-24 px-6">
        <section className="max-w-6xl mx-auto pt-8 pb-20">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
              {t("about.title")}
            </h1>
            <p className="text-lg text-muted-foreground">
              {t("about.subtitle")}
            </p>
          </div>

          <div className="w-full">
            {/* Add invisible anchors for each team member */}
            {team.map((member) => {
              const anchor = member.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
              return (
                <div
                  key={`anchor-${member.name}`}
                  id={anchor}
                  className="scroll-mt-24"
                  style={{ position: 'absolute', top: '-96px' }}
                />
              );
            })}
            <TeamGrid team={team} title={t("about.teamTitle")} showBio={true} interactive={false} />
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/privacy"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block mx-3"
            >
              {t("footer.privacy")}
            </Link>
            <Link
              to="/terms"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors inline-block mx-3"
            >
              {t("footer.terms")}
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-6 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center">
          <p className="text-sm text-muted-foreground mb-4 sm:mb-0">
            © {new Date().getFullYear()} Kinesis LTD. All rights reserved.
          </p>
          <div className="flex gap-4">
            <Link to="/about" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t("footer.about")}
            </Link>
            <Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t("footer.privacy")}
            </Link>
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t("footer.terms")}
            </Link>
            <a href="mailto:legal@knesis.com" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              {t("footer.contact")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
